/**
 * Shared data-testid vocabulary + builder.
 * Single source of truth consumed by packages/web and packages/mobile components;
 * the generated slug string is manually mirrored into fiji-test/Web.json locatorValue
 * entries (Java side can't import this module) per .github/locator-testid-rollout.skill.md.
 */
export const FEATURES = {
    WIDGET_MANAGEMENT: 'widgetManagement',
    DASHBOARD_MANAGEMENT: 'dashboardManagement',
    ENTITLEMENTS: 'entitlements',
    FEATURE_PACKAGES: 'featurePackages',
    OLD_ENTITLEMENTS: 'oldEntitlements',
    LOAD_GROUPS: 'loadGroups',
    LOAD_BALANCING: 'loadBalancing',
    RAMP_SHED: 'rampShed',
    ICONS_MANAGEMENT: 'iconsManagement',
    CHANNEL_LIBRARY: 'channelLibrary',
    CHANNEL_SELECTOR: 'channelSelector',
    AUTOMATIONS: 'automations',
    NOTIFICATIONS: 'notifications',
    AUDIT_LOGS: 'auditLogs',
    QUARANTINE: 'quarantine',
    SERVICE_CONTACT: 'serviceContact',
    REPORTS: 'reports',
    DOCUMENTS: 'documents',
    SETTINGS: 'settings',
    ORG_FLOW_MODAL: 'orgFlowModal',
    AGGREGATED_DETAILS: 'aggregatedDetails',
    CONSUMPTION_BREAKDOWN: 'consumptionBreakdown',
    ENERGY_FLOW: 'energyFlow',
    LOAD_TREND: 'loadTrend',
    QR_DETAILS: 'qrDetails',
    WEATHER_DETAILS: 'weatherDetails',
    CHART: 'chart',
    REALM_MANAGEMENT: 'realmManagement',
    GROUP_MANAGEMENT: 'groupManagement',
    MY_PROFILE: 'myProfile',
    EDIT_USER_PROFILE: 'editUserProfile',
    USERS_AND_ROLES: 'usersAndRoles',
    ADD_DEVICE: 'addDevice',
    CUSTOM_URL: 'customUrl',
    SCHEDULING: 'scheduling',
    ORGANIZATION_MANAGEMENT: 'organizationManagement',
    MANAGE_ORGANIZATION: 'manageOrganization',
    DEVICE_DETAILS: 'deviceDetails',
    DEVICE_MIGRATION: 'deviceMigration',
    TIMELINE_DETAILS: 'timelineDetails',
    COMMON: 'common',
    NAV: 'nav',
    UPCOMING_EVENTS: 'upcomingEvents',
} as const;

export const COMPONENTS = {
    PAGE: 'page',
    TABLE: 'table',
    DIALOG: 'dialog',
    FORM: 'form',
    MENU: 'menu',
    SEARCH: 'search',
    CARD: 'card',
    TAB: 'tab',
    DRAWER: 'drawer',
    BANNER: 'banner',
    TOASTER: 'toaster',
    CALENDAR: 'calendar',
    MODAL: 'modal',
} as const;

export const ELEMENTS = {
    BTN: 'btn',
    INPUT: 'input',
    ICON: 'icon',
    ROW: 'row',
    CELL: 'cell',
    TITLE: 'title',
    CHECKBOX: 'checkbox',
    DROPDOWN: 'dropdown',
    LINK: 'link',
    TEXT: 'text',
    CHIP: 'chip',
    STEP: 'step',
    RADIO: 'radio',
    TOGGLE: 'toggle',
} as const;

export const ACTIONS = {
    ADD: 'add',
    EDIT: 'edit',
    DELETE: 'delete',
    CREATE: 'create',
    CONFIRM: 'confirm',
    CANCEL: 'cancel',
    SAVE: 'save',
    SUBMIT: 'submit',
    VIEW: 'view',
    EXPORT: 'export',
    DOWNLOAD: 'download',
    NEXT: 'next',
    BACK: 'back',
    DISABLE: 'disable',
    ENABLE: 'enable',
    APPROVE: 'approve',
    SELECT: 'select',
    FILTER: 'filter',
    SORT: 'sort',
} as const;

type Feature = (typeof FEATURES)[keyof typeof FEATURES];
type Component = (typeof COMPONENTS)[keyof typeof COMPONENTS];
type ElementType = (typeof ELEMENTS)[keyof typeof ELEMENTS];
type Action = (typeof ACTIONS)[keyof typeof ACTIONS];

type TestIdParams = {
    feature: Feature;
    component: Component;
    element?: ElementType;
    action?: Action;
    name?: string;
    id?: string | number;
};

function sanitizeName(name: string): string {
    return name
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
}

export function rawTestId({ feature, component, element, action, name, id }: TestIdParams): string {
    const segments = [feature, component, element, action, name ? sanitizeName(name) : undefined, id];
    return segments.filter((segment) => segment !== undefined && segment !== null && segment !== '').join('_');
}

export function testId(params: TestIdParams): string {
    return `//*[@data-testid='${rawTestId(params)}']`;
}

/**
 * CCN-neutral helpers.
 * Every branch a call site would otherwise inline (`?.`, `??`, `||`, `? :`) is absorbed here
 * once, so adding a data-testid never raises the host component's cyclomatic complexity.
 */

type IdCarrier = Record<string, unknown> | null | undefined;

/** Nullish-safe id read: use instead of `row?.id` inside a `tid.*()` call. */
export function idOf(source: unknown, key = 'id'): string | number | undefined {
    const value = (source as IdCarrier)?.[key];
    if (typeof value === 'string' || typeof value === 'number') {
        return value;
    }
    return undefined;
}

/** Consumer-override resolution: use instead of `props['data-testid'] ?? fallback`. */
export function resolveTestId(provided: unknown, fallback: string): string {
    return typeof provided === 'string' && provided !== '' ? provided : fallback;
}

/** Variant selection: use instead of an inline `cond ? idA : idB` in JSX. */
export function pickTestId(condition: unknown, whenTrue: string, whenFalse: string): string {
    return condition ? whenTrue : whenFalse;
}

type FeatureBuilder = (
    component: Component,
    element?: ElementType,
    action?: Action,
    name?: string,
    id?: string | number
) => string;

function makeFeatureBuilder(feature: Feature): FeatureBuilder {
    return (component, element, action, name, id) => rawTestId({ feature, component, element, action, name, id });
}

export const tid = Object.fromEntries(
    Object.values(FEATURES).map((feature) => [feature, makeFeatureBuilder(feature)])
) as Record<Feature, FeatureBuilder>;

const documentsTestId = tid[FEATURES.DOCUMENTS];
const normalizeDocumentId = (id: string | number | undefined): string | number | undefined =>
    typeof id === 'string' ? sanitizeName(id) : id;

export const DOCUMENTS_TEST_IDS = {
    reportRow: (id: string | number | undefined): string =>
        documentsTestId(COMPONENTS.PAGE, ELEMENTS.ROW, ACTIONS.VIEW, 'report', normalizeDocumentId(id)),
    documentRow: (id: string | number | undefined): string =>
        documentsTestId(COMPONENTS.PAGE, ELEMENTS.ROW, ACTIONS.VIEW, 'document', normalizeDocumentId(id)),
    downloadDocument: (id: string | number | undefined): string =>
        documentsTestId(COMPONENTS.PAGE, ELEMENTS.BTN, ACTIONS.DOWNLOAD, 'document', normalizeDocumentId(id)),
    downloadReport: (id: string | number | undefined): string =>
        documentsTestId(COMPONENTS.PAGE, ELEMENTS.BTN, ACTIONS.DOWNLOAD, 'report', normalizeDocumentId(id)),
} as const;

const serviceContactTestId = tid[FEATURES.SERVICE_CONTACT];

export const SERVICE_CONTACT_TEST_IDS = {
    page: {
        editButton: serviceContactTestId(COMPONENTS.PAGE, ELEMENTS.BTN, ACTIONS.EDIT, 'service-contacts'),
        expandIcon: serviceContactTestId(COMPONENTS.PAGE, ELEMENTS.ICON, undefined, undefined, 'expand'),
        cancelButton: serviceContactTestId(COMPONENTS.PAGE, ELEMENTS.BTN, ACTIONS.CANCEL),
        saveButton: serviceContactTestId(COMPONENTS.PAGE, ELEMENTS.BTN, ACTIONS.SAVE),
    },
    table: {
        root: serviceContactTestId(COMPONENTS.TABLE),
        row: (index: number): string =>
            serviceContactTestId(COMPONENTS.TABLE, ELEMENTS.ROW, undefined, undefined, index),
        dragHandle: (index: number): string =>
            serviceContactTestId(COMPONENTS.TABLE, ELEMENTS.ICON, undefined, 'drag-handle', index),
    },
} as const;

const deviceDetailsTestId = tid[FEATURES.DEVICE_DETAILS];

const upcomingEventsTestId = tid[FEATURES.UPCOMING_EVENTS];

const deviceMigrationTestId = tid[FEATURES.DEVICE_MIGRATION];

export const DEVICE_MIGRATION_TEST_IDS = {
    page: {
        columnFilterMenu: (name: string): string =>
            deviceMigrationTestId(COMPONENTS.MENU, undefined, undefined, `column-filter-${name}`),
        columnFilterInput: (name: string): string =>
            deviceMigrationTestId(COMPONENTS.FORM, ELEMENTS.INPUT, ACTIONS.FILTER, name),
        statusFilter: (name: string): string =>
            deviceMigrationTestId(COMPONENTS.FORM, ELEMENTS.CHECKBOX, ACTIONS.FILTER, `status-${name}`),
        calendar: deviceMigrationTestId(COMPONENTS.CALENDAR),
        emptyState: deviceMigrationTestId(COMPONENTS.TABLE, undefined, undefined, 'empty-state'),
        actionsMenu: deviceMigrationTestId(COMPONENTS.MENU),
    },
    table: {
        headerCell: (name: string): string =>
            deviceMigrationTestId(COMPONENTS.TABLE, ELEMENTS.CELL, undefined, `header-${name}`),
        sort: (name: string): string => deviceMigrationTestId(COMPONENTS.TABLE, ELEMENTS.BTN, ACTIONS.SORT, name),
        select: (id: string): string =>
            deviceMigrationTestId(COMPONENTS.TABLE, ELEMENTS.CHECKBOX, ACTIONS.SELECT, 'device', id),
        status: (id: string): string => deviceMigrationTestId(COMPONENTS.TABLE, ELEMENTS.ICON, undefined, 'status', id),
        cell: (name: string, id: string): string =>
            deviceMigrationTestId(COMPONENTS.TABLE, ELEMENTS.CELL, undefined, name, id),
    },
} as const;

export const SERVICE_ADMIN_UPCOMING_EVENT_TEST_IDS = {
    page: {
        eventStatement: upcomingEventsTestId(COMPONENTS.PAGE, ELEMENTS.TEXT, undefined, 'event-statement'),
    },
    table: {
        predictionCell: (id: string | number | undefined): string =>
            upcomingEventsTestId(COMPONENTS.TABLE, ELEMENTS.CELL, ACTIONS.VIEW, 'prediction', id),
        organizationCell: (id: string | number | undefined): string =>
            upcomingEventsTestId(COMPONENTS.TABLE, ELEMENTS.CELL, ACTIONS.VIEW, 'organization', id),
    },
} as const;

export const SERVICE_ADMIN_DEVICE_TEST_IDS = {
    page: {
        root: deviceDetailsTestId(COMPONENTS.PAGE),
        dashboardTab: deviceDetailsTestId(COMPONENTS.TAB, ELEMENTS.BTN, ACTIONS.SELECT, 'dashboard'),
        alarmsTab: deviceDetailsTestId(COMPONENTS.TAB, ELEMENTS.BTN, ACTIONS.SELECT, 'alarms'),
        upcomingTab: deviceDetailsTestId(COMPONENTS.TAB, ELEMENTS.BTN, ACTIONS.SELECT, 'upcoming'),
        heartbeatsTab: deviceDetailsTestId(COMPONENTS.TAB, ELEMENTS.BTN, ACTIONS.SELECT, 'heartbeats'),
        commentsTab: deviceDetailsTestId(COMPONENTS.TAB, ELEMENTS.BTN, ACTIONS.SELECT, 'comments'),
    },
    action: {
        viewInCustomerPortal: deviceDetailsTestId(COMPONENTS.PAGE, ELEMENTS.BTN, ACTIONS.VIEW, 'customer-portal'),
        addComment: deviceDetailsTestId(COMPONENTS.PAGE, ELEMENTS.BTN, ACTIONS.ADD, 'comment'),
        more: deviceDetailsTestId(COMPONENTS.MENU, ELEMENTS.BTN, undefined, 'more'),
        deleteDevice: deviceDetailsTestId(COMPONENTS.MENU, ELEMENTS.BTN, ACTIONS.DELETE, 'device'),
        viewInCustomerPortalMenu: deviceDetailsTestId(COMPONENTS.MENU, ELEMENTS.BTN, ACTIONS.VIEW, 'customer-portal'),
    },
    dashboard: {
        propertiesLoading: deviceDetailsTestId(COMPONENTS.CARD, ELEMENTS.TEXT, undefined, 'properties-loading'),
        alarmsPreview: deviceDetailsTestId(COMPONENTS.CARD, undefined, undefined, 'alarms-preview'),
        upcomingPreview: deviceDetailsTestId(COMPONENTS.CARD, undefined, undefined, 'upcoming-preview'),
        commentsPreview: deviceDetailsTestId(COMPONENTS.CARD, undefined, undefined, 'comments-preview'),
        viewAllAlarms: deviceDetailsTestId(COMPONENTS.CARD, ELEMENTS.BTN, ACTIONS.VIEW, 'all-alarms'),
        viewAllUpcoming: deviceDetailsTestId(COMPONENTS.CARD, ELEMENTS.BTN, ACTIONS.VIEW, 'all-upcoming'),
        viewAllComments: deviceDetailsTestId(COMPONENTS.CARD, ELEMENTS.BTN, ACTIONS.VIEW, 'all-comments'),
        contactsTable: deviceDetailsTestId(COMPONENTS.TABLE, undefined, undefined, 'contacts'),
    },
    alarms: {
        search: deviceDetailsTestId(COMPONENTS.SEARCH, ELEMENTS.INPUT, undefined, 'alarms'),
        download: deviceDetailsTestId(COMPONENTS.PAGE, ELEMENTS.BTN, ACTIONS.DOWNLOAD, 'alarms'),
        table: deviceDetailsTestId(COMPONENTS.TABLE, undefined, undefined, 'alarms'),
        row: (id: string | number) => deviceDetailsTestId(COMPONENTS.TABLE, ELEMENTS.ROW, undefined, undefined, id),
    },
    upcoming: {
        search: deviceDetailsTestId(COMPONENTS.SEARCH, ELEMENTS.INPUT, undefined, 'upcoming'),
        download: deviceDetailsTestId(COMPONENTS.PAGE, ELEMENTS.BTN, ACTIONS.DOWNLOAD, 'upcoming'),
        table: deviceDetailsTestId(COMPONENTS.TABLE, undefined, undefined, 'upcoming'),
        row: (id: string | number) => deviceDetailsTestId(COMPONENTS.TABLE, ELEMENTS.ROW, undefined, undefined, id),
    },
    heartbeats: {
        search: deviceDetailsTestId(COMPONENTS.SEARCH, ELEMENTS.INPUT, undefined, 'heartbeats'),
        download: deviceDetailsTestId(COMPONENTS.PAGE, ELEMENTS.BTN, ACTIONS.DOWNLOAD, 'heartbeats'),
        table: deviceDetailsTestId(COMPONENTS.TABLE, undefined, undefined, 'heartbeats'),
        row: (id: string | number) => deviceDetailsTestId(COMPONENTS.TABLE, ELEMENTS.ROW, undefined, undefined, id),
        detail: deviceDetailsTestId(COMPONENTS.DRAWER, undefined, undefined, 'heartbeats-detail'),
        detailClose: deviceDetailsTestId(COMPONENTS.DRAWER, ELEMENTS.BTN, ACTIONS.CANCEL, 'heartbeats-detail'),
        detailDownload: deviceDetailsTestId(COMPONENTS.DRAWER, ELEMENTS.BTN, ACTIONS.DOWNLOAD, 'heartbeats-email'),
        detailAttachment: (id: string | number) =>
            deviceDetailsTestId(COMPONENTS.DRAWER, ELEMENTS.ROW, undefined, 'heartbeats-attachment', id),
    },
    comments: {
        card: (id: string | number) => deviceDetailsTestId(COMPONENTS.CARD, undefined, undefined, 'comment', id),
    },
} as const;

const orgManagementTestId = tid[FEATURES.ORGANIZATION_MANAGEMENT];

export const EDIT_ORG_DETAILS_TEST_IDS = {
    firstName: orgManagementTestId(COMPONENTS.FORM, ELEMENTS.INPUT, undefined, 'first-name'),
    lastName: orgManagementTestId(COMPONENTS.FORM, ELEMENTS.INPUT, undefined, 'last-name'),
    email: orgManagementTestId(COMPONENTS.FORM, ELEMENTS.INPUT, undefined, 'email'),
    areaCode: orgManagementTestId(COMPONENTS.FORM, ELEMENTS.INPUT, undefined, 'area-code'),
    phoneNumber: orgManagementTestId(COMPONENTS.FORM, ELEMENTS.INPUT, undefined, 'phone-number'),
    location: orgManagementTestId(COMPONENTS.FORM, ELEMENTS.INPUT, undefined, 'location'),
    myLocation: orgManagementTestId(COMPONENTS.FORM, ELEMENTS.BTN, undefined, 'my-location'),
    latitude: orgManagementTestId(COMPONENTS.FORM, ELEMENTS.INPUT, undefined, 'latitude'),
    longitude: orgManagementTestId(COMPONENTS.FORM, ELEMENTS.INPUT, undefined, 'longitude'),
    search: orgManagementTestId(COMPONENTS.FORM, ELEMENTS.BTN, undefined, 'search'),
    cancel: orgManagementTestId(COMPONENTS.PAGE, ELEMENTS.BTN, ACTIONS.CANCEL),
    save: orgManagementTestId(COMPONENTS.PAGE, ELEMENTS.BTN, ACTIONS.SAVE),
} as const;

const realmManagementTestId = tid[FEATURES.REALM_MANAGEMENT];

export const REALM_MANAGEMENT_TEST_IDS = {
    apiAccountForm: {
        nameInput: realmManagementTestId(COMPONENTS.FORM, ELEMENTS.INPUT, undefined, 'name'),
        descriptionInput: realmManagementTestId(COMPONENTS.FORM, ELEMENTS.INPUT, undefined, 'description'),
        expirationDatePicker: realmManagementTestId(COMPONENTS.FORM, ELEMENTS.INPUT, undefined, 'expiration-date'),
        roleTypeSelect: realmManagementTestId(COMPONENTS.FORM, ELEMENTS.DROPDOWN, undefined, 'role-type'),
        organizationSelect: realmManagementTestId(COMPONENTS.FORM, ELEMENTS.DROPDOWN, undefined, 'organization'),
        realmSelect: realmManagementTestId(COMPONENTS.FORM, ELEMENTS.DROPDOWN, undefined, 'realm'),
        roleSelect: realmManagementTestId(COMPONENTS.FORM, ELEMENTS.DROPDOWN, undefined, 'role'),
        cancelButton: realmManagementTestId(COMPONENTS.FORM, ELEMENTS.BTN, ACTIONS.CANCEL),
        createButton: realmManagementTestId(COMPONENTS.FORM, ELEMENTS.BTN, ACTIONS.CREATE),
    },
} as const;
